import { safeParseFloat, safeParseInt } from "encamp-shared/src/utils/safeParseNumber";
import { Issue } from "generated-graphql/graphql";

import {
  ExtractionDraftValue,
  ExtractionFieldConfidence,
  ExtractionFieldSpec,
  ExtractionResult,
  ExtractionReviewSchema,
  ExtractionReviewValues,
  extractedBaseline,
  fromDraftValue,
  isBlankDraft,
  rowConfidenceKey,
  toDraftValue,
} from "./types";

/**
 * One repeated row in the form. `extractedIndex` is the row's position in the
 * extraction result — null on a row the reviewer added — and travels with the
 * row so its confidence stays anchored to the cell the extractor produced, not
 * to wherever the row ends up after rows are added or deleted. It sits beside
 * the values rather than among them so a column key can never collide with it,
 * and it reaches the caller for the same reason it exists here: see
 * `ExtractionReviewedRow`.
 */
export interface ExtractionReviewRow {
  extractedIndex: number | null;
  values: Record<string, ExtractionDraftValue>;
}

/**
 * The react-hook-form shape behind the grid. Values are keyed by the caller's
 * own field keys, so a form path is `values.<fieldKey>` or
 * `rows.<groupKey>.<index>.values.<columnKey>`.
 */
export interface ExtractionReviewFormData {
  values: Record<string, ExtractionDraftValue>;
  rows: Record<string, ExtractionReviewRow[]>;
}

/** Whatever `useWatch` hands back mid-edit, which is deeply partial. */
export interface PartialExtractionReviewRow {
  extractedIndex?: number | null;
  values?: Record<string, ExtractionDraftValue | undefined>;
}

export interface PartialExtractionReviewFormData {
  values?: Record<string, ExtractionDraftValue | undefined>;
  rows?: Record<string, PartialExtractionReviewRow[] | undefined>;
}

/**
 * A field key becomes a react-hook-form path segment, so a key containing a dot
 * would address a nested object that the flat value record doesn't have. Callers
 * get a dev-time warning rather than a silently blank field.
 */
export function warnOnUnsupportedKeys(schema: ExtractionReviewSchema): void {
  const keys = [
    ...schema.sections.flatMap((section) => section.fields.map((field) => field.key)),
    ...(schema.rowGroups ?? []).flatMap((group) => [
      group.key,
      ...group.columns.map((column) => column.key),
    ]),
  ];
  const dotted = keys.filter((key) => key.includes("."));
  if (dotted.length > 0) {
    console.warn(
      `ExtractionReviewGrid: field keys cannot contain "." — these will not round-trip: ${dotted.join(", ")}`
    );
  }
}

/** Form path of a single-value field. */
export function fieldPath(fieldKey: string): `values.${string}` {
  return `values.${fieldKey}`;
}

/** Form path of a repeated group's array. */
export function rowGroupPath(groupKey: string): `rows.${string}` {
  return `rows.${groupKey}`;
}

/** Form path of one cell of a repeated row. */
export function rowCellPath(groupKey: string, index: number, columnKey: string): `rows.${string}` {
  return `rows.${groupKey}.${index}.values.${columnKey}`;
}

/** Seed the form from an extraction, leaving fields the extractor missed empty. */
export function buildDefaultValues(
  schema: ExtractionReviewSchema,
  extraction: ExtractionResult | null | undefined
): ExtractionReviewFormData {
  const values: Record<string, ExtractionDraftValue> = {};
  for (const section of schema.sections) {
    for (const field of section.fields) {
      values[field.key] = toDraftValue(field, extraction?.values?.[field.key]);
    }
  }

  const rows: Record<string, ExtractionReviewRow[]> = {};
  for (const group of schema.rowGroups ?? []) {
    rows[group.key] = (extraction?.rows?.[group.key] ?? []).map((extractedRow, extractedIndex) => ({
      extractedIndex,
      values: Object.fromEntries(
        group.columns.map((column) => [column.key, toDraftValue(column, extractedRow[column.key])])
      ),
    }));
  }

  return { values, rows };
}

/** A blank repeated row, which has no extracted candidate behind it. */
export function buildBlankRow(columnKeys: readonly string[]): ExtractionReviewRow {
  return {
    extractedIndex: null,
    values: Object.fromEntries(columnKeys.map((key) => [key, null])),
  };
}

/** Convert the reviewed drafts back to the caller's value types. */
export function toReviewedValues(
  schema: ExtractionReviewSchema,
  data: ExtractionReviewFormData
): ExtractionReviewValues {
  const values: ExtractionReviewValues["values"] = {};
  for (const section of schema.sections) {
    for (const field of section.fields) {
      values[field.key] = fromDraftValue(field, data.values?.[field.key]);
    }
  }

  const rows: ExtractionReviewValues["rows"] = {};
  for (const group of schema.rowGroups ?? []) {
    rows[group.key] = (data.rows?.[group.key] ?? []).map((row) => ({
      extractedIndex: row.extractedIndex ?? null,
      values: Object.fromEntries(
        group.columns.map((column) => [
          column.key,
          fromDraftValue(column, row.values?.[column.key]),
        ])
      ),
    }));
  }

  return { values, rows };
}

/** True when the reviewer has left nothing that could be committed. */
export function isEmptyReview(data: PartialExtractionReviewFormData): boolean {
  if (Object.values(data.values ?? {}).some((value) => !isBlankDraft(value))) return false;
  return !Object.values(data.rows ?? {}).some((rows) =>
    (rows ?? []).some((row) =>
      Object.values(row?.values ?? {}).some((value) => !isBlankDraft(value))
    )
  );
}

export function indexConfidence(
  confidence: readonly ExtractionFieldConfidence[] | undefined
): Map<string, number> {
  const index = new Map<string, number>();
  for (const entry of confidence ?? []) index.set(entry.field, entry.score);
  return index;
}

/**
 * Whether two drafts hold the same thing. A value the extractor left empty and
 * one the reviewer typed into and then cleared are the same state, so null,
 * empty string, and whitespace all compare equal.
 */
export function draftsEqual(a: ExtractionDraftValue, b: ExtractionDraftValue): boolean {
  if (typeof a === "boolean" || typeof b === "boolean") return a === b;
  const left = a == null ? "" : a.trim();
  const right = b == null ? "" : b.trim();
  return left === right;
}

/**
 * Has the reviewer supplied something other than what the extractor proposed?
 *
 * Derived by comparing the two values rather than read from react-hook-form's
 * `dirtyFields`, deliberately. RHF recomputes dirty state positionally against
 * the original defaults, so removing a repeated row marks every surviving row
 * cell dirty even though nobody touched them; and its comparison is strict, so
 * clearing a field the extractor already left empty would count as an edit. Both
 * would wrongly retire a "needs review" flag. A comparison against the value's
 * own extracted candidate can't drift in either way.
 */
export function isReviewed(
  spec: ExtractionFieldSpec,
  current: ExtractionDraftValue | undefined,
  extracted: ExtractionDraftValue
): boolean {
  // A number is compared as a number: retyping 5 as 5.0 is not a correction, and
  // treating it as one both retires the low-confidence flag and pollutes the
  // per-field edit signal the extraction pipeline records for tuning.
  if (spec.kind === "number") {
    const left = typeof current === "string" ? safeParseFloat(current) : null;
    const right = typeof extracted === "string" ? safeParseFloat(extracted) : null;
    if (left != null || right != null) return left !== right;
  }
  return !draftsEqual(current ?? null, extracted);
}

/** True while a value is still an unreviewed low-confidence extraction. */
export function isFlagged(
  score: number | null,
  reviewed: boolean,
  lowConfidenceThreshold: number
): boolean {
  if (reviewed || score == null) return false;
  return score < lowConfidenceThreshold;
}

/**
 * How many values are still flagged. Shares `isFlagged` and `isReviewed` with the
 * per-field indicators, so the banner's count and the highlights are computed
 * from one definition and cannot disagree.
 */
export function countFlaggedValues({
  schema,
  extraction,
  data,
  confidence,
  lowConfidenceThreshold,
}: {
  schema: ExtractionReviewSchema;
  extraction: ExtractionResult;
  data: PartialExtractionReviewFormData;
  confidence: Map<string, number>;
  lowConfidenceThreshold: number;
}): number {
  const baseline = extractedBaseline(extraction);
  let count = 0;

  for (const section of schema.sections) {
    for (const field of section.fields) {
      const reviewed = isReviewed(
        field,
        data.values?.[field.key],
        toDraftValue(field, baseline.values?.[field.key])
      );
      if (isFlagged(confidence.get(field.key) ?? null, reviewed, lowConfidenceThreshold))
        count += 1;
    }
  }

  for (const group of schema.rowGroups ?? []) {
    for (const row of data.rows?.[group.key] ?? []) {
      const extractedIndex = row?.extractedIndex;
      if (extractedIndex == null) continue;
      const extractedRow = baseline.rows?.[group.key]?.[extractedIndex];
      for (const column of group.columns) {
        const reviewed = isReviewed(
          column,
          row.values?.[column.key],
          toDraftValue(column, extractedRow?.[column.key])
        );
        const score =
          confidence.get(rowConfidenceKey(group.key, extractedIndex, column.key)) ?? null;
        if (isFlagged(score, reviewed, lowConfidenceThreshold)) count += 1;
      }
    }
  }

  return count;
}

/**
 * Re-key a caller's validation issues onto the form's paths.
 *
 * The caller validates in its own namespace — `"phLow"`, or a row cell — because
 * it has no reason to know how the grid nests its form. This bridges the two so
 * an issue lands on the right input, the same way `reviewFieldDiffs` bridges the
 * manifest comparators' field names to the manifest review form.
 *
 * Both row-cell forms are accepted, because the two systems that produce issues
 * number their lists differently and each is right in its own terms: the
 * compliance engine uses `constituents.0.maxPercent`, matching how
 * react-hook-form addresses a field array, while the extraction pipeline uses
 * `constituents[0].casNumber`, matching how its field paths index a stored JSON
 * payload.
 *
 * An issue whose key matches nothing in the schema keeps its key, so it still
 * reaches the issue list rather than disappearing.
 */
export function toFormIssues(schema: ExtractionReviewSchema, issues: readonly Issue[]): Issue[] {
  const rowGroupKeys = new Set((schema.rowGroups ?? []).map((group) => group.key));
  const fieldKeys = new Set(
    schema.sections.flatMap((section) => section.fields.map((field) => field.key))
  );

  return issues.map((issue) => {
    const key = issue.key;
    if (key == null) return issue;
    if (fieldKeys.has(key)) return { ...issue, key: fieldPath(key) };

    const indexed =
      /^([^.[\]]+)\[(\d+)\]\.(.+)$/.exec(key) ?? /^([^.[\]]+)\.(\d+)\.(.+)$/.exec(key);
    if (indexed != null && rowGroupKeys.has(indexed[1])) {
      return { ...issue, key: rowCellPath(indexed[1], safeParseInt(indexed[2]) ?? 0, indexed[3]) };
    }
    return issue;
  });
}
